import { useState } from "react";
import { comments, poll } from "../../../mocks/portalData";

type CommentItem = {
  user: string;
  role: string;
  time: string;
  stance: string;
  text: string;
  like: number;
  replies: number;
  repliesData: { user: string; text: string }[];
};

const stanceStyle: Record<string, string> = {
  中立: "bg-secondary-100 text-secondary-800",
  站队: "bg-accent-100 text-accent-800",
  质疑: "bg-primary-100 text-primary-800",
};

function CommentCard({ item }: { item: CommentItem }) {
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(item.like);
  const [showReply, setShowReply] = useState(false);

  const toggleLike = () => {
    if (liked) setCount((c) => c - 1);
    else setCount((c) => c + 1);
    setLiked(!liked);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground-950 text-sm font-black text-background-50">
          {item.user.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-foreground-950">{item.user}</span>
            <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">
              {item.role}
            </span>
            <span className={"whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold " + stanceStyle[item.stance]}>
              {item.stance}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-foreground-400">{item.time}</p>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-foreground-800">{item.text}</p>

      {showReply && item.repliesData.length > 0 && (
        <div className="mt-3 space-y-2 rounded-md bg-background-100 p-3">
          {item.repliesData.map((r, i) => (
            <div key={i} className="flex gap-2 text-sm">
              <span className="shrink-0 font-bold text-foreground-700">{r.user}：</span>
              <span className="text-foreground-600">{r.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 border-t border-background-200 pt-2.5">
        <button
          type="button"
          onClick={toggleLike}
          className={
            "flex items-center gap-1 whitespace-nowrap text-xs font-bold transition-colors " +
            (liked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600")
          }
        >
          <i className={liked ? "ri-thumb-up-fill text-sm" : "ri-thumb-up-line text-sm"}></i>
          {count}
        </button>
        <button
          type="button"
          onClick={() => setShowReply(!showReply)}
          className="flex items-center gap-1 whitespace-nowrap text-xs font-bold text-foreground-500 hover:text-foreground-900"
        >
          <i className="ri-chat-3-line text-sm"></i>
          {item.replies} 讨论
        </button>
        <button
          type="button"
          className="ml-auto flex items-center gap-1 whitespace-nowrap text-xs font-bold text-foreground-400 hover:text-primary-600"
        >
          <i className="ri-share-line text-sm"></i>
          转发
        </button>
      </div>
    </div>
  );
}

export default function CommentsSection() {
  const [activePoll, setActivePoll] = useState(poll.items[0].label);

  return (
    <section id="comments" className="w-full border-t border-background-200/70 bg-background-100">
      <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-accent-500"></span>
            <h3 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">热评讨论区</h3>
            <span className="ml-1 hidden items-center gap-1 whitespace-nowrap rounded-full bg-background-200 px-2.5 py-0.5 text-xs font-bold text-foreground-600 sm:flex">
              <i className="ri-fire-line"></i>
              {poll.total.toLocaleString()} 人参与表态
            </span>
          </div>
          <button
            type="button"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-background-50 hover:bg-accent-600"
          >
            <i className="ri-add-line text-base"></i>
            发表观点
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
          {/* poll + 发言 */}
          <aside className="flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h4 className="text-sm font-black text-foreground-950">本场焦点 · 你怎么看</h4>
              <div className="mt-3 space-y-2">
                {poll.items.map((item) => {
                  const selected = activePoll === item.label;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setActivePoll(item.label)}
                      className="block w-full text-left"
                    >
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className={"font-bold " + (selected ? "text-foreground-950" : "text-foreground-600")}>
                          {item.label}
                        </span>
                        <span className="font-black text-foreground-700">{item.value}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-background-200">
                        <div
                          className={
                            "h-full rounded-full transition-all " +
                            (item.tone === "accent"
                              ? "bg-accent-500"
                              : item.tone === "primary"
                                ? "bg-primary-500"
                                : "bg-secondary-500")
                          }
                          style={{ width: item.value + "%" }}
                        ></div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <form className="rounded-lg border border-background-200 bg-background-50 p-4">
              <label className="text-xs font-black text-foreground-900">加入讨论</label>
              <input
                type="text"
                name="website_alt"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="website_alt"
              />
              <textarea
                rows={4}
                maxLength={500}
                placeholder="理性吃瓜，先看全上下文再开麦……"
                className="mt-2 w-full resize-none rounded-md border border-background-300/80 bg-background-50 p-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>
              <button
                type="submit"
                className="mt-2 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
              >
                <i className="ri-send-plane-line text-sm"></i>
                发布热评
              </button>
            </form>
          </aside>

          {/* comment list */}
          <div className="flex flex-col gap-3">
            {comments.map((item, idx) => (
              <CommentCard key={idx} item={item} />
            ))}
            <button
              type="button"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:border-background-400"
            >
              查看更多热评
              <i className="ri-refresh-line text-base"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}